import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreDeleteActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    operationTimeoutMetadata,
    useTempFileRenameCheckboxMetadata,
    getCommonFileTypeStoreReadSettingControls,
    getCommonBinaryStoreReadSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { binaryMetadata } from '../dataset/binary-metadata';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import {
    avro,
    delimited,
    excel,
    json,
    orc,
    parquet, xml
} from 'app/registries/dataflow/dataflow-file-format-metadata';
import { sftpDisableChunkingInfoLink } from 'app/shared/constants/links.const';
import { ConnectionAuthKind, sftpAuthTypes, SftpPrivateKeyType, GlobalVariables as shellGlobalVariables, shellStringResources } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { driverVersionPreviewVersionOptions, driverVersions } from '../consts/tabular-consts';
import { GlobalVariables } from 'app/services/globals';
import { compact } from 'lodash';

const defaultAuthOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.Basic_DisplayText,
        value: sftpAuthTypes.Basic
    },
    {
        label: stringResources.Authentication_SSH_Public_Key,
        value: sftpAuthTypes.SshPublicKey
    }
];

const multiAuthOptions: Array<ISelectOption<string>> = [
    ...defaultAuthOptions,
    {
        label: stringResources.Authentication_Multi_Factor_Auth,
        value: sftpAuthTypes.MultiFactor
    }
];

const dynamicUsername: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const dynamicPassword: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'parameterizable',
        attribute: 'password',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, shellStringResources.Sftp_DisplayText),
        stringResources.ModelName_Password)
};

const dynamicPrivateKeyContent: ControlMetadata = {
    type: 'select-switch',
    bind: 'privateKeyType',
    label: stringResources.SFTPServer_LinkedService_PrivateKeyType,
    hideDropdownWhenOptionsLessThan2: true,
    options: [
        {
            label: stringResources.SFTPServer_LinkedService_UseKeyContent,
            value: SftpPrivateKeyType.Content
        },
        {
            label: stringResources.SFTPServer_LinkedService_UseKeyPath,
            value: SftpPrivateKeyType.Path,
            hide: '{ isAzureIR && !isExistingResourceUsingPrivateKeyPath }'
        }
    ],
    isLabelVertical: true,
    defaultValue: SftpPrivateKeyType.Content,
    cases: [
        {
            caseValue: SftpPrivateKeyType.Content,
            children: [
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    bind: 'privateKeyContent',
                    label: stringResources.ModelName_PrivateKeyContent,
                    child: {
                        type: 'parameterizable',
                        attribute: 'privateKeyContent',
                        disableAddParameter: true,
                        child: {
                            type: 'key-file-content',
                            label: stringResources.ModelName_PrivateKeyContent,
                            required: true,
                            isLabelVertical: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    },
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, shellStringResources.Sftp_DisplayText),
                        stringResources.SFTPServer_LinkedService_Base64PrivateKeyContent)
                }
            ]
        }, {
            caseValue: SftpPrivateKeyType.Path,
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'privateKeyPath',
                        label: shellStringResources.ModelName_PrivateKeyPath,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                }
            ]
        }
    ]
};

const dynamicPassphrase: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'passPhrase',
    label: stringResources.ModelName_PassPhrase,
    child: {
        type: 'parameterizable',
        attribute: 'passPhrase',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_PassPhrase,
            isLabelVertical: true
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, shellStringResources.Sftp_DisplayText),
        stringResources.ModelName_PassPhrase)
};


const hostControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'host',
        label: stringResources.ModelName_Host,
        required: true,
        placeholder: stringResources.SFTPServer_LinkedService_Host_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const portControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        defaultValue: 22,
        min: 0,
        max: 65535
    }
};

const getAuthenticationTypeControl = (driverVersion: driverVersions): ControlMetadata => ({
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: driverVersion === driverVersions.v2 ? defaultAuthOptions : multiAuthOptions,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true,
    defaultValue: 'Basic',
    cases: [
        {
            caseValue: sftpAuthTypes.Basic,
            children: [dynamicUsername, dynamicPassword]
        },
        {
            caseValue: sftpAuthTypes.SshPublicKey,
            children: [
                dynamicUsername,
                dynamicPrivateKeyContent,
                dynamicPassphrase
            ]
        },
        {
            caseValue: sftpAuthTypes.MultiFactor,
            children: [
                dynamicUsername,
                dynamicPassword,
                dynamicPrivateKeyContent,
                dynamicPassphrase
            ]
        }
    ]
});


const skipHostKeyValidationControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'skipHostKeyValidation',
    label: shellStringResources.ModelName_SkipHostKeyValidation,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.SFTPServer_LinkedService_SkipHostKeyValidation,
            value: true
        },
        {
            label: stringResources.SFTPServer_LinkedService_NotSkipHostKeyValidation,
            value: false
        }
    ],
    defaultValue: true,
    cases: [
        {
            caseValue: false,
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'hostKeyFingerprint',
                        label: shellStringResources.ModelName_HostKeyFingerprint,
                        isLabelVertical: true,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        }
    ]
}

const versionControl: ControlMetadata = {
    type: 'radio-switch',
    bind: 'version',
    label: stringResources.VersionLabel,
    isLabelVertical: true,
    options: driverVersionPreviewVersionOptions,
    defaultValue: driverVersions.v2,
    hide: !shellGlobalVariables.enableSFTPV2,
    cases: [
        {
            caseValue: driverVersions.v2,
            children: [hostControl, portControl, skipHostKeyValidationControl, getAuthenticationTypeControl(driverVersions.v2)]
        },
        {
            caseValue: driverVersions.v1,
            children: [hostControl, portControl, skipHostKeyValidationControl, getAuthenticationTypeControl(driverVersions.v1)]
        }
    ]
};

export const sftpServerMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.sftp.linkedServiceType,
        displayName: shellStringResources.Sftp_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-SFTP'
        },
        isFirstClass: true,
        supportedInTrident: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'SFTP' as ResourceKind
        },
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.Basic,
            GlobalVariables.enableKeyPairAuthForSFTP ? ConnectionAuthKind.KeyPair : undefined
        ]),
        category: ['File'],
        supportIncrementalBinaryCopy: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.fileSystem.copySourceType],
        supportGetMetadataActivity: true,
        supportDatasetV2: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        dataflowSource: [avro, delimited, excel, json, orc, parquet, xml],
        dataflowSink: [avro, delimited, json, orc, parquet],
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        getMetadataActivityReadProperties: ['modifiedDatetimeStart', 'modifiedDatetimeEnd'],
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(false, false, false, false, false, { infoLink: sftpDisableChunkingInfoLink }),
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(false, false, false, false, true, { infoLink: sftpDisableChunkingInfoLink })
        },
        storeWriteSettingControls: [...commonFileTypeStoreWriteSettingControls, operationTimeoutMetadata, useTempFileRenameCheckboxMetadata],
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: [...commonBinaryStoreWriteSettingControls, operationTimeoutMetadata, useTempFileRenameCheckboxMetadata]
        },
        supportFirstClassParameterExperience: true,
        supportOPDG: GlobalVariables.enableOPDG,
        controls: [versionControl,
            ...[
                hostControl,
                portControl,
                skipHostKeyValidationControl,
                getAuthenticationTypeControl(driverVersions.v1)
            ].map((control) => ({
                ...control,
                hide: shellGlobalVariables.enableSFTPV2
            }))]
    };
    return metadata;
};
